'use client'
import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconArrowForward } from '@repo/design-system-cf/components/icons'
import { Typography } from '@repo/design-system-cf/components/typography'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import { DOES_NOT_HAVE_ALL_ACCESS } from '@repo/web-ui/components/static-variants-renderer/utils/conditions'
import { useFormatter, useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'
import { LFS_URL } from '@web/utils/env'
import { useCurrentCurrency } from '@repo/cf-user/hooks/use-current-currency'
import { CurrencyCode } from '@repo/cf-user/gen/types'

type BannerVariants = 'graphics' | 'crafts' | 'crafts-bottom'

export function BannerWithSearch({
  variant,
  totalProductCount: _totalProductCount = 0,
  searchBar = null,
}: {
  variant: BannerVariants
  totalProductCount?: number
  searchBar?: React.ReactNode
}) {
  const format = useFormatter()
  const t = useTranslations('components.category_landing_page')
  const currency = useCurrentCurrency()
  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.code || CurrencyCode.Usd,
  }).format(0)

  const totalProductCount = format.number(_totalProductCount)
  return (
    <>
      <InlineStaticVariant
        conditions={[DOES_NOT_HAVE_ALL_ACCESS]}
        getVariant={({ conditionsMet }) =>
          conditionsMet ? (
            <>
              <CategoryBanner variant={variant}>
                <>
                  <div className="flex flex-col gap-4">
                    <Typography
                      asChild
                      variant="cf-title-06"
                      className={tw('m-0', {
                        'text-cf-white': variant === 'graphics',
                        'text-gray-purple-500': variant === 'crafts',
                        'text-center text-[#4c425f]': variant === 'crafts-bottom',
                      })}
                    >
                      <h1>{t('heading_try_all_access')}</h1>
                    </Typography>
                    <ul
                      className={tw('flex flex-col gap-2', {
                        'text-cf-white': variant === 'graphics',
                        'text-gray-purple-500': variant === 'crafts',
                        'text-[#4c425f]': variant === 'crafts-bottom',
                      })}
                    >
                      <Typography
                        asChild
                        variant="body-01-m"
                        className={tw('text-12 @3xl:text-14', {
                          'text-cf-white': variant === 'graphics',
                          'text-gray-purple-500': variant === 'crafts',
                          'text-[#4c425f]': variant === 'crafts-bottom',
                        })}
                      >
                        <li>
                          <IconArrowForward className="mr-4 inline h-4 w-4 shrink-0 !stroke-2" />
                          {t.rich('unlimited_access_to', {
                            strong: children => <span className="font-extrabold">{children}</span>,
                            count: totalProductCount,
                          })}
                        </li>
                      </Typography>
                      <Typography
                        asChild
                        variant="body-01-m"
                        className={tw('text-12 @3xl:text-14', {
                          'text-cf-white': variant === 'graphics',
                          'text-gray-purple-500': variant === 'crafts',
                          'text-[#4c425f]': variant === 'crafts-bottom',
                        })}
                      >
                        <li>
                          <IconArrowForward className="mr-4 inline h-4 w-4 shrink-0 !stroke-2" />
                          {t('instant_access_all_new')}
                        </li>
                      </Typography>
                      <Typography
                        asChild
                        variant="body-01-m"
                        className={tw('text-12 @3xl:text-14', {
                          'text-cf-white': variant === 'graphics',
                          'text-gray-purple-500': variant === 'crafts',
                          'text-[#4c425f]': variant === 'crafts-bottom',
                        })}
                      >
                        <li>
                          <IconArrowForward className="mr-4 inline h-4 w-4 shrink-0 !stroke-2" />
                          {t('personal_and_commercial_use')}
                        </li>
                      </Typography>
                      <Typography
                        asChild
                        variant="body-01-m"
                        className={tw('text-12 @3xl:text-14', {
                          'text-cf-white': variant === 'graphics',
                          'text-gray-purple-500': variant === 'crafts',
                          'text-[#4c425f]': variant === 'crafts-bottom',
                        })}
                      >
                        <li>
                          <IconArrowForward className="mr-4 inline h-4 w-4 shrink-0 !stroke-2" />
                          {t('including')}
                        </li>
                      </Typography>
                    </ul>
                  </div>
                  <div
                    className={tw(
                      'text-cf-white @3xl:items-center @3xl:pt-4 @3xl:shrink-0 flex flex-grow flex-col gap-2',
                      {
                        'text-cf-white': variant === 'graphics',
                        'text-gray-purple-500': variant === 'crafts',
                        '@3xl:pt-0 items-center text-[#4c425f]': variant === 'crafts-bottom',
                      },
                    )}
                  >
                    {variant === 'crafts-bottom' ? null : (
                      <div className="@3xl:text-center">
                        <Typography
                          variant="cf-label-01"
                          className="text-30 @3xl:text-16 !leading-none"
                        >
                          {t('risk_free_trial')}
                        </Typography>
                        <Typography
                          variant="cf-label-01"
                          className="text-40 @3xl:text-[3.5rem] font-bold uppercase leading-none"
                        >
                          {t('free_trial')}
                        </Typography>
                      </div>
                    )}
                    <div
                      className={tw(
                        '@3xl:text-center flex flex-col items-stretch gap-2 self-stretch py-4',
                        {
                          'pt-0': variant === 'crafts-bottom',
                        },
                      )}
                    >
                      <FreeTrialBannerCTA variant={variant}>
                        {t('start_downloading', { zeroPrice })}
                      </FreeTrialBannerCTA>
                      <Typography
                        variant="cf-label-01"
                        className={tw('text-11', {
                          'text-cf-white': variant === 'graphics',
                          'text-gray-purple-500': variant === 'crafts',
                          'text-center text-[#4c425f]': variant === 'crafts-bottom',
                        })}
                      >
                        {t('1_month_trial')}
                      </Typography>
                    </div>
                  </div>
                </>
              </CategoryBanner>
              {searchBar ? (
                <div id="search-section" className="@container w-full bg-[#f6f7fb] px-2">
                  <div className="@5xl:-translate-y-1/2 @5xl:py-0 mx-auto max-w-3xl py-8">
                    <div className="bg-cf-white">{searchBar}</div>
                  </div>
                </div>
              ) : null}
            </>
          ) : searchBar ? (
            <div id="search-section" className="@container w-full bg-[#f6f7fb] px-2">
              <div className="mx-auto max-w-3xl py-8">
                <div className="bg-cf-white">{searchBar}</div>
              </div>
            </div>
          ) : null
        }
      />
    </>
  )
}

function CategoryBanner({
  variant,
  children,
}: {
  variant: BannerVariants
  children: React.ReactNode
}) {
  return (
    <div
      className={tw('@container w-full', {
        'bg-[#67b876]': variant === 'graphics',
        'bg-[#f5e589]': variant === 'crafts' || variant === 'crafts-bottom',
      })}
      style={{
        backgroundImage:
          // oxlint-disable-next-line no-nested-ternary
          variant === 'crafts'
            ? `url(${LFS_URL}/web/pages/content/crafts-header.png)`
            : variant === 'crafts-bottom'
              ? `url(${LFS_URL}/web/pages/content/crafts-footer-banner.png)`
              : `url(${LFS_URL}/web/pages/content/graphics-header.jpg)`,
        backgroundSize: variant === 'graphics' || variant === 'crafts-bottom' ? 'cover' : undefined,
        backgroundPosition: variant === 'graphics' ? 'center' : 'bottom right',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div
        className={tw(
          '@3xl:gap-8 @3xl:flex-row @3xl:justify-between @3xl:mx-auto @3xl:max-w-7xl flex flex-col gap-16 px-8 py-14',
          { '@3xl:py-10 @3xl:px-12 gap-2 px-6 py-6': variant === 'crafts-bottom' },
        )}
      >
        {children}
      </div>
    </div>
  )
}

function FreeTrialBannerCTA({
  children,
  variant,
}: {
  children?: React.ReactNode
  variant: BannerVariants
}) {
  const { startFreeTrial } = useStartFreeTrial()
  return (
    <Button
      data-testid={variant === 'graphics' || variant === 'crafts' ? 'hero-cta' : 'bottom-cta'}
      variant={
        variant === 'graphics' || variant === 'crafts' || variant === 'crafts-bottom'
          ? 'yellow-cta'
          : 'primary'
      }
      className="text-20 @3xl:max-w-[30rem]"
      onClick={async () => {
        savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.GRAPHICS_BOTTOM_BANNER)
        sendAmplitudeEvent(AmplitudeEvents.FREE_TRIAL_BUTTON_CLICKED)
        await startFreeTrial()
      }}
    >
      {children}
    </Button>
  )
}
